import React, { memo, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import cn from 'classnames';
import {
    ContentBlockFragment,
    KolLeaderboardDataFragment,
    KolProfileBaseFragment,
} from '@graphqlTypes/sitecore';
import { withBasePath, withFullPath } from '@src/common/path-convert';
import { useTranslation } from 'react-i18next';
import { nonPageTracker } from '@src/adobe-launch/data-layer-provider';
import { removeCIDUrl, removeflashUrl } from '@src/utils/util';
import { TrackingEvent } from '@src/adobe-launch/types';
import { jsBridge } from '@src/utils/js-bridge';
import { convertArticleInfo } from '@src/hooks/tracker/use-article-tracker';
import useWindowSize from '@src/utils/useWindowSize';
import videoStore from '@mobx/stores/video-store';
import { enUS, zhHK } from 'date-fns/locale';
import { format } from 'date-fns';
type Props = {
    key: number;
    kolData: KolLeaderboardDataFragment;
    contentBlock: ContentBlockFragment;
    comingSoonMode: boolean;
};

interface TableProps {
    name: string;
    value: string;
}

const LeaderBoardItem = ({ kolData, comingSoonMode, contentBlock }: Props) => {
    const router = useRouter();
    const { t, i18n } = useTranslation('kol');
    const { width } = useWindowSize();

    const isEn = i18n.language.toLowerCase() === 'en-us';
    const SPad = width >= 630;
    const padSzie = SPad;

    const {
        race1stSrc,
        race1stText,
        race1stType,
        race1stVideoNative,
        race1stVideoNativePip,
        race2ndSrc,
        race2ndText,
        race2ndType,
        race2ndVideoNative,
        race2ndVideoNativePip,
        kolProfileLink,
    } = kolData;

    const [isOpen, setIsOpen] = useState(false);
    const [tableHeaders1, setTableHeaders1] = useState<string[]>([]);
    const [tableRowData1, setTableRowData1] = useState<string[][]>([]);
    const [tableHeaders2, setTableHeaders2] = useState<string[]>([]);
    const [tableRowData2, setTableRowData2] = useState<string[][]>([]);

    const kolProfileItem: KolProfileBaseFragment | undefined = kolProfileLink?.targetItem;
    const kolProText = kolProfileItem?.leaderboardDescription?.rendered || '';
    const textBreakWords = !kolProText.includes('<br/>');
    const htmlContent = kolProText
        .split(/<\s*br\s*\/?\s*>/gi)
        .map((s) => s.replace(/<\/?[^>]+>/g, '').trim())
        .filter((s) => s.length > 0);
    const resultText = processHtmlContent(htmlContent);
    const isSingleText = htmlContent.length == 1 || htmlContent.length == 0 ? true : false;

    // handle leader board description, max 15 En or 8 CN characters
    function processHtmlContent(html: string[]): string {
        return html
            .map((segment) => {
                const hasChinese = /[\u4E00-\u9FFF]/.test(segment);
                //if has chinese, then recognize it as CN text
                let truncated = '';
                if (hasChinese) {
                    truncated = Array.from(segment).slice(0, 8).join('');
                } else {
                    truncated = segment.match(/[a-zA-Z0-9\s]{0,15}/)?.toString() || '';
                }
                // console.log(segment, truncated);
                return truncated;
            })
            .join('<br/>');
    }

    /** 白板show/hide */
    const toggleOpen = () => {
        //isOpen = false when expand, = true when collapse
        nonPageTracker(
            {
                eventID: isOpen ? TrackingEvent.CollapseKOLprofile : TrackingEvent.ExpandKOLprofile,
                kolName: kolProfileItem?.name,
            },
            { article: convertArticleInfo(contentBlock) }
        );

        setIsOpen((prev) => !prev);
    };
    /** link跳转 */
    const linkTo = (type: string, url: string) => {
        if (type !== 'Video') {
            if (type === 'Internal') {
                router.push(removeCIDUrl(`/${url}${location.search}`), undefined);
            } else {
                jsBridge.appOpenExternalBrowser(url);
            }
        }
    };
    /** videoNoNative-VideoJS */
    const videoNoNative = (type?: string, videoTitle?: string, link?: string) => {
        videoStore.setDefaults();
        type && videoStore.setVideoType(type);
        if (type === 'race' && link && videoTitle) {
            const raceId = link + ' ' + videoTitle;
            // console.log(raceId);
            videoStore.setraceID(raceId);
        }
        contentBlock && videoStore.setVideoContentBlock(contentBlock);
        kolProfileItem && videoStore.setkolName(kolProfileItem.name);
    };
    /** videoNative-jsBridge */
    /** only record RaceNo for Leaderboard-Betpick video */
    /** Leaderboard no relataed betpick info, so take race video src and title as RaceNo */
    const videoNative = (
        videoNative: boolean,
        videoNativePip: boolean,
        link: string,
        videoTitle: string,
        type?: string
    ) => {
        const RaceId = link + ' ' + videoTitle;
        nonPageTracker(
            {
                eventID: TrackingEvent.VideoPlay,
                eventValue: type
                    ? type === 'race'
                        ? 'Leaderboard-Betpick'
                        : type === 'Masterpiece'
                        ? 'Leaderboard-Masterpiece'
                        : undefined
                    : undefined,
                raceID: type && type === 'race' ? RaceId : '',
                kolName: kolProfileItem?.name,
                videoURL: removeflashUrl(link ?? ''),
            },
            { article: convertArticleInfo(contentBlock) }
        );
        return (
            videoNative &&
            jsBridge.appOpenNativePlayer(link ?? '', videoTitle ?? '', videoNativePip ?? false)
        );
    };
    /** headers 数据封装 */
    const headersFun = (data: TableProps[] | []) => {
        return data.length > 0 ? data.map((item) => item.name) : [];
    };
    /** 转换值并分组为 rowData */
    const rowData = (data: TableProps[] | []) => {
        return data.length > 0
            ? data.reduce((acc, item) => {
                  const values = decodeURIComponent(item.value).split(',');
                  values.forEach((value, index) => {
                      if (!acc[index]) {
                          acc[index] = [];
                      }
                      acc[index].push(value);
                  });
                  return acc;
              }, [] as string[][])
            : [];
    };

    /** 日期格式話 */
    const formatDate = (dateString: string) => {
        const dateParts = dateString.split('/');
        if (dateParts.length !== 2) {
            console.error(`Invalid date format: ${dateString}`);
            return dateString; // 返回默认值
        }
        const [day, month] = dateParts.map(Number);
        const date = new Date(new Date().getFullYear(), month - 1, day); // 创建日期对象

        return isEn
            ? format(date, 'd MMM', { locale: enUS })
            : format(date, 'M月d日', { locale: zhHK });
    };
    /** 数据处理 */
    useEffect(() => {
        if (kolProfileItem) {
            setTableHeaders1(headersFun(kolProfileItem.rMTable?.values || []));
            setTableRowData1(rowData(kolProfileItem.rMTable?.values || []));
            setTableHeaders2(headersFun(kolProfileItem.lRTable?.values || []));
            setTableRowData2(rowData(kolProfileItem.lRTable?.values || []));
        }
    }, [kolProfileItem]);

    return (
        <div className='mt-4'>
            {/* Card */}
            <div
                className={`w-full h-[67px] ss:h-[58.81px] sd:h-[67px] flex items-center bg-white shadow-lg ${
                    isOpen ? 'rounded-t-lg' : 'rounded-lg'
                }`}
            >
                {/* Rank */}
                <div className='flex flex-col items-center justify-center w-[22px] md:w-[36px] rounded-l-lg text-white translate-x-[3px]'>
                    {kolProfileItem?.trendIIcon?.value && (
                        <img
                            className='mb-1 w-[10px]'
                            src={withBasePath(
                                `${
                                    kolProfileItem?.trendIIcon?.value === 'Up'
                                        ? '/images/kol/i-up.svg'
                                        : '/images/kol/i-downG.svg'
                                }`
                            )}
                            alt=''
                            loading='lazy'
                        />
                    )}
                    <div className='text-[16px] font-bold text-[#003C84]'>
                        {kolProfileItem?.rank?.value}
                    </div>
                </div>
                {/* KOL */}
                <div
                    className={cn(
                        'flex w-[135px] ss:w-[118.5px] sd:w-[135px] md:w-[204px] h-full items-center relative',
                        SPad && '!w-[204px]'
                    )}
                    onClick={() => {
                        router.push(
                            removeCIDUrl(`/kol-profile/${kolProfileItem?.name}${location.search}`)
                        );
                    }}
                >
                    {width > 0 && (
                        <img
                            src={withFullPath(
                                padSzie
                                    ? kolProfileItem?.leaderboardPadIcon?.src || ''
                                    : kolProfileItem?.leaderboardAppIcon?.src || ''
                            )}
                            alt='bg'
                            className='w-full z-10 absolute left-0 bottom-0'
                            loading='lazy'
                        />
                    )}
                    <div
                        className={cn(
                            'z-30 pl-5 pt-1 sd:pl-5 ss:pl-3',
                            isSingleText ? 'sd:pt-[18px] ss:pt-[16px]' : 'sd:pt-1 ss:pt-2'
                        )}
                    >
                        <div className='inline-flex justify-start items-center py-[2px] px-1 bg-white bg-opacity-40 rounded-full'>
                            <div className='text-[#003C84] text-[11px] md:text-xs font-bold pr-1 flex items-center'>
                                <img
                                    src={withBasePath('/images/kol/i-crown.svg')}
                                    alt=''
                                    className='w-4 h-4 mr-[2px] sd:w-4 sd:h-4 ss:w-[14px] ss:h-[14px]'
                                    loading='lazy'
                                />
                                <div className='whitespace-nowrap'>
                                    {kolProfileItem?.monthChampionCount?.value || 0}
                                </div>
                            </div>
                            <div className='text-[#003C84] text-[11px] md:text-xs font-bold flex items-center'>
                                <img
                                    src={withBasePath('/images/kol/i-crown2.svg')}
                                    alt=''
                                    className='w-4 h-4 mr-[2px] sd:w-4 sd:h-4 ss:w-[14px] ss:h-[14px]'
                                    loading='lazy'
                                />
                                <div className='whitespace-nowrap'>
                                    {kolProfileItem?.highestStrikeRateCount?.value || 0}
                                </div>
                            </div>
                        </div>
                        <div
                            className='mt-[-2px]'
                            style={{
                                lineHeight: 1.2,
                                textShadow: '0px 2px 6px rgba(0, 0, 0, 0.3)',
                            }}
                        >
                            <div className='font-bold text-[13px] pt-1 ss:text-[11px] md:text-[14px] text-white'>
                                {kolProfileItem?.kolName?.value}
                            </div>
                            <div
                                className={cn(
                                    'w-[100px] h-[calc(2*0.74rem)] pt-[2px] font-bold text-[10px] ss:text-[8px] text-white ml-[-2px] pl-[2px]',
                                    textBreakWords && 'break-words'
                                )}
                                dangerouslySetInnerHTML={{
                                    __html: resultText || '',
                                }}
                            ></div>
                        </div>
                    </div>
                </div>
                {/* Score StrikeRate*/}
                {padSzie ? (
                    <div className='flex flex-1'>
                        <div className='flex w-1/2 text-[#003C84] font-bold text-[18px]'>
                            <div className='w-1/2 flex justify-center'>
                                {kolProfileItem?.strikeRate?.value}
                            </div>
                            <div className='w-1/2 flex justify-center'>
                                {kolProfileItem?.score?.value}
                            </div>
                        </div>
                        {/* video */}
                        <div className='flex w-1/2 justify-center items-center'>
                            {comingSoonMode || !(race1stSrc?.value || race2ndSrc?.value) ? (
                                <div className='text-[#003C84] w-[80%] h-[26px] text-[11px] font-bold flex justify-center items-center opacity-50'>
                                    <img
                                        className='mr-[2px] w-5 h-5'
                                        src={withBasePath('/images/kol/i-comingSoon.svg')}
                                        alt='up'
                                        loading='lazy'
                                    />
                                    <div className='text-center'>
                                        <div>{t('profile.betPickVideo')}</div>
                                        <div>{t('profile.comingSoon')}</div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {race1stSrc?.value && (
                                        <div
                                            className={cn(
                                                'flex justify-center items-center whitespace-nowrap text-[14px] font-medium text-black',
                                                race1stType?.value === 'Video' &&
                                                    !race1stVideoNative?.boolValue &&
                                                    'video-pop'
                                            )}
                                            data-poster={race1stSrc?.value}
                                            onClick={() => {
                                                if (race1stType?.value === 'Video') {
                                                    return race1stVideoNative?.boolValue
                                                        ? videoNative(
                                                              race1stVideoNative?.boolValue ??
                                                                  false,
                                                              race1stVideoNativePip?.boolValue ??
                                                                  false,
                                                              race1stSrc?.value ?? '',
                                                              race1stText?.value ?? '',
                                                              'race'
                                                          )
                                                        : videoNoNative(
                                                              'race',
                                                              race1stText?.value ?? '',
                                                              race1stSrc?.value ?? ''
                                                          );
                                                }
                                                linkTo(
                                                    race1stType?.value ?? 'Video',
                                                    race1stSrc?.value ?? ''
                                                );
                                            }}
                                        >
                                            <img
                                                className='w-6 mr-[2px]'
                                                src={withBasePath('/images/kol/i-play.svg')}
                                                alt=''
                                                loading='lazy'
                                            />
                                            {race1stText?.value}
                                        </div>
                                    )}
                                    {race1stSrc?.value && race2ndSrc?.value && (
                                        <div className='h-5 w-[1px] bg-[#E0E0E0] mx-2' />
                                    )}
                                    {race2ndSrc?.value && (
                                        <div
                                            className={cn(
                                                'flex justify-center items-center whitespace-nowrap text-[14px] font-medium text-black',
                                                race2ndType?.value === 'Video' &&
                                                    !race2ndVideoNative?.boolValue &&
                                                    'video-pop'
                                            )}
                                            data-poster={race2ndSrc?.value}
                                            onClick={() => {
                                                if (race2ndType?.value === 'Video') {
                                                    return race2ndVideoNative?.boolValue
                                                        ? videoNative(
                                                              race2ndVideoNative?.boolValue ??
                                                                  false,
                                                              race2ndVideoNativePip?.boolValue ??
                                                                  false,
                                                              race2ndSrc?.value ?? '',
                                                              race2ndText?.value ?? '',
                                                              'race'
                                                          )
                                                        : videoNoNative(
                                                              'race',
                                                              race2ndText?.value ?? '',
                                                              race2ndSrc?.value ?? ''
                                                          );
                                                }
                                                linkTo(
                                                    race2ndType?.value ?? 'Video',
                                                    race2ndSrc?.value ?? ''
                                                );
                                            }}
                                        >
                                            <img
                                                className='w-6 mr-[2px]'
                                                src={withBasePath('/images/kol/i-play.svg')}
                                                alt=''
                                                loading='lazy'
                                            />
                                            {race2ndText?.value}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className='flex-1'>
                        <div className='w-full flex items-center text-[#003C84] font-bold text-[18px] ss:text-[16px] sd:text-[18px]'>
                            <div className='w-1/2 px-1 text-center'>
                                {kolProfileItem?.strikeRate?.value}
                            </div>
                            <div className='w-1/2 px-1 text-center'>
                                {kolProfileItem?.score?.value}
                            </div>
                        </div>

                        {/* video */}
                        <div className='flex justify-center items-center -translate-x-[2px]'>
                            {comingSoonMode || !(race1stSrc?.value || race2ndSrc?.value) ? (
                                <div className='text-[#003C84] h-[26px] text-[11px] ss:text-[10px] sd:text-[11px] font-bold flex items-center opacity-50 text-center leading-[1.2]'>
                                    <img
                                        className='mr-[2px] w-5 h-5'
                                        src={withBasePath('/images/kol/i-comingSoon.svg')}
                                        alt='up'
                                        loading='lazy'
                                    />
                                    <div className='text-center'>
                                        <div>{t('profile.betPickVideo')}</div>
                                        <div>{t('profile.comingSoon')}</div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {race1stSrc?.value && (
                                        <div
                                            className={cn(
                                                'flex justify-center whitespace-nowrap items-center text-[13px] sd:text-[13px] ss:text-[12px] font-medium text-black',
                                                race1stType?.value === 'Video' &&
                                                    !race1stVideoNative?.boolValue &&
                                                    'video-pop'
                                            )}
                                            data-poster={race1stSrc?.value}
                                            onClick={() => {
                                                if (race1stType?.value === 'Video') {
                                                    return race1stVideoNative?.boolValue
                                                        ? videoNative(
                                                              race1stVideoNative?.boolValue ??
                                                                  false,
                                                              race1stVideoNativePip?.boolValue ??
                                                                  false,
                                                              race1stSrc?.value ?? '',
                                                              race1stText?.value ?? '',
                                                              'race'
                                                          )
                                                        : videoNoNative(
                                                              'race',
                                                              race1stText?.value ?? '',
                                                              race1stSrc?.value ?? ''
                                                          );
                                                }
                                                linkTo(
                                                    race1stType?.value ?? 'Video',
                                                    race1stSrc?.value ?? ''
                                                );
                                            }}
                                        >
                                            <img
                                                className='w-5 ss:w-4 sd:w-5 mr-[2px] ss:mt-[-2px] sd:mt-0'
                                                src={withBasePath('/images/kol/i-play.svg')}
                                                alt=''
                                                loading='lazy'
                                            />
                                            {race1stText?.value}
                                        </div>
                                    )}
                                    {race1stSrc?.value && race2ndSrc?.value && (
                                        <div className='h-5 w-[1px] bg-[#E0E0E0] mx-1' />
                                    )}
                                    {race2ndSrc?.value && (
                                        <div
                                            className={cn(
                                                'flex justify-center items-center whitespace-nowrap text-[13px] sd:text-[13px] ss:text-[12px] font-medium text-black',
                                                race2ndType?.value === 'Video' &&
                                                    !race2ndVideoNative?.boolValue &&
                                                    'video-pop'
                                            )}
                                            data-poster={race2ndSrc?.value}
                                            onClick={() => {
                                                if (race2ndType?.value === 'Video') {
                                                    return race2ndVideoNative?.boolValue
                                                        ? videoNative(
                                                              race2ndVideoNative?.boolValue ??
                                                                  false,
                                                              race2ndVideoNativePip?.boolValue ??
                                                                  false,
                                                              race2ndSrc?.value ?? '',
                                                              race2ndText?.value ?? '',
                                                              'race'
                                                          )
                                                        : videoNoNative(
                                                              'race',
                                                              race2ndText?.value ?? '',
                                                              race2ndSrc?.value ?? ''
                                                          );
                                                }
                                                linkTo(
                                                    race2ndType?.value ?? 'Video',
                                                    race2ndSrc?.value ?? ''
                                                );
                                            }}
                                        >
                                            <img
                                                className='w-5 ss:w-4 sd:w-5 mr-[2px] ss:mt-[-2px] sd:mt-0'
                                                src={withBasePath('/images/kol/i-play.svg')}
                                                alt=''
                                                loading='lazy'
                                            />
                                            {race2ndText?.value}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* show/hide Whiteboard */}
                <div
                    className={`w-6 ss:w-5 sd:w-6 h-full bg-[#E2EDF6] ${
                        isOpen ? 'rounded-tr-lg' : 'rounded-r-lg'
                    } flex justify-center items-center`}
                    onClick={toggleOpen}
                >
                    <img
                        className={`w-5 h-5 transition-transform duration-300 ${
                            isOpen ? 'rotate-180' : ''
                        }`}
                        src={withBasePath('/images/kol/i-down.svg')}
                        alt=''
                        loading='lazy'
                    />
                </div>
            </div>
            {/* Whiteboard */}
            <div
                className={`${
                    isOpen && 'border-t border-t-[#F0F0F0]'
                } bg-white rounded-b-lg shadow-lg`}
            >
                <div
                    className={`px-3 transition-max-height duration-300 ease-in-out overflow-hidden ${
                        isOpen ? 'max-h-[90000000px]' : 'max-h-0'
                    }`}
                >
                    {/* table1 */}
                    <div className='pt-3'>
                        <div className='text-sm text-black font-medium mb-2 flex items-center justify-start'>
                            {kolProfileItem?.rMIcon?.src && (
                                <img
                                    className={`w-6 h-6 mr-1`}
                                    src={withFullPath(kolProfileItem?.rMIcon?.src || '')}
                                    alt=''
                                    loading='lazy'
                                />
                            )}
                            {kolProfileItem?.rMTitle?.value}
                        </div>

                        <div className='overflow-hidden rounded-[4px] border border-[#E2EDF6]'>
                            <table className='min-w-full bg-white '>
                                <thead>
                                    <tr>
                                        {tableHeaders1.map((header, index) => (
                                            <th
                                                key={index}
                                                className={cn(
                                                    padSzie
                                                        ? 'text-[12px]'
                                                        : 'text-[11px] ss:text-[10px] sd:text-[11px]',
                                                    'py-2 px-1 bg-[#E2EDF6] text-black relative font-medium'
                                                )}
                                            >
                                                {formatDate(header)}
                                                {index !== tableHeaders1.length - 1 && (
                                                    <div className='absolute top-1/2 right-0 transform -translate-y-1/2 w-px h-[11px] bg-white' />
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                {tableRowData1.length > 0 && (
                                    <tbody>
                                        <tr className=''>
                                            {tableRowData1[0].map((i, index) => (
                                                <td
                                                    key={index}
                                                    className='py-1 px-1 text-sm text-black font-medium text-center relative'
                                                    style={{ borderBottom: '1px solid #E2EDF6' }}
                                                >
                                                    {i === 'ST' ? t('ST') : t('HV')}
                                                    {index !== tableRowData1[0].length - 1 && (
                                                        <div className='absolute top-1/2 right-0 transform -translate-y-1/2 w-px h-[11px] bg-[#E2EDF6]' />
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                        <tr className=''>
                                            {tableRowData1[1].map((i, index1) => (
                                                <td
                                                    key={index1}
                                                    className={`py-1 text-sm ${
                                                        i === '1st'
                                                            ? 'text-[#FF9C00]'
                                                            : 'text-[#555555]'
                                                    }  font-medium  relative`}
                                                >
                                                    <div className='flex justify-center items-center'>
                                                        {i}
                                                        {index1 !== tableRowData1[1].length - 1 && (
                                                            <div className='absolute top-1/2 right-0 transform -translate-y-1/2 w-px h-[11px] bg-[#E2EDF6]' />
                                                        )}
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                )}
                            </table>
                        </div>
                    </div>
                    {/* table2 */}
                    <div className={cn('pt-3', !kolProfileItem?.bREnabled?.boolValue && 'pb-3')}>
                        <div className='text-sm text-black font-medium mb-2 flex items-center justify-start'>
                            {kolProfileItem?.lRIcon?.src && (
                                <img
                                    className={`w-6 h-6 mr-1`}
                                    src={withFullPath(kolProfileItem?.lRIcon?.src || '')}
                                    alt=''
                                    loading='lazy'
                                />
                            )}
                            {kolProfileItem?.lastResultTitle?.value}
                        </div>

                        <div
                            style={{ WebkitOverflowScrolling: 'touch' }}
                            className='overflow-x-auto rounded-[4px] border border-[#E2EDF6]'
                        >
                            <table
                                style={{ width: tableHeaders2.length * 60 }}
                                className='min-w-full bg-white '
                            >
                                <thead>
                                    <tr>
                                        {tableHeaders2.map((header, index) => (
                                            <th
                                                style={{
                                                    position: index === 0 ? 'sticky' : undefined,
                                                    willChange: index === 0 ? 'auto' : undefined,
                                                    zIndex: index === 0 ? 20 : undefined,
                                                    left: index === 0 ? 0 : undefined,
                                                }}
                                                key={index}
                                                className='py-2 w-[60px] text-center bg-[#E2EDF6] text-black text-sm relative'
                                            >
                                                {header}
                                                {index !== tableHeaders2.length - 1 && (
                                                    <div className='absolute top-1/2 right-0 transform -translate-y-1/2 w-px h-[11px] bg-white' />
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                {tableRowData2.length > 0 && (
                                    <tbody>
                                        <tr className=''>
                                            {tableRowData2[0].map((i, index) => (
                                                <td
                                                    style={{
                                                        position:
                                                            index === 0 ? 'sticky' : undefined,
                                                        willChange:
                                                            index === 0 ? 'auto' : undefined,
                                                        zIndex: index === 0 ? 20 : undefined,
                                                        left: index === 0 ? 0 : undefined,
                                                        borderBottom: '1px solid #E2EDF6',

                                                        // index === 0 && {
                                                        //     position: 'sticky',
                                                        //     willChange: 'auto',
                                                        //     zIndex: 20,
                                                        //     //   boxShadow: '1px 0 0 #E2EDF6',
                                                        //     left: 0,
                                                        //     backgroundColor: '#ffffff',
                                                        // }
                                                    }}
                                                    key={index}
                                                    className={cn(
                                                        'py-1 px-1 w-[60px] text-black bg-white font-medium text-center relative',
                                                        index === 0 ? 'text-[11px]' : 'text-sm'
                                                    )}
                                                >
                                                    {i}
                                                    {index !== tableRowData2[0].length - 1 && (
                                                        <div className='absolute top-1/2 right-0 transform -translate-y-1/2 w-px h-[11px] bg-[#E2EDF6]' />
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                        <tr className=''>
                                            {tableRowData2[1].map((i, index1) => (
                                                <td
                                                    style={{
                                                        position:
                                                            index1 === 0 ? 'sticky' : undefined,
                                                        willChange:
                                                            index1 === 0 ? 'auto' : undefined,
                                                        zIndex: index1 === 0 ? 20 : undefined,
                                                        left: index1 === 0 ? 0 : undefined,
                                                    }}
                                                    key={index1}
                                                    className={cn(
                                                        `py-1 px-1 w-[60px] font-medium relative text-center bg-white`,
                                                        index1 === 0 ? 'text-[11px]' : 'text-sm'
                                                    )}
                                                >
                                                    <div className='flex justify-center items-center'>
                                                        {i}
                                                        {index1 !== tableRowData2[1].length - 1 && (
                                                            <div className='absolute top-1/2 right-0 transform -translate-y-1/2 w-px h-[11px] bg-[#E2EDF6]' />
                                                        )}
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                )}
                            </table>
                        </div>
                    </div>
                    {/* text */}
                    {kolProfileItem?.bREnabled?.boolValue && (
                        <div className='py-3'>
                            <div className='text-sm text-black font-medium mb-2 flex items-center justify-start'>
                                {kolProfileItem?.bRIcon?.src && (
                                    <img
                                        className={`w-6 h-6 mr-1`}
                                        src={withFullPath(kolProfileItem?.bRIcon?.src || '')}
                                        alt=''
                                        loading='lazy'
                                    />
                                )}
                                {kolProfileItem?.bestResultTitle?.value}
                            </div>
                            <div className='rounded-[4px] border border-[#E2EDF6] px-2 py-2'>
                                <div className='flex justify-between items-center'>
                                    <div className='flex items-center'>
                                        {kolProfileItem?.bRSubIcon?.src && (
                                            <img
                                                className={`w-6 h-6 mr-1`}
                                                src={withFullPath(
                                                    kolProfileItem?.bRSubIcon?.src || ''
                                                )}
                                                alt=''
                                                loading='lazy'
                                            />
                                        )}
                                        <div className='flex flex-col'>
                                            <div className='text-[#003C84] text-sm font-bold'>
                                                {kolProfileItem?.bRSubTitle?.rendered}
                                            </div>
                                            <div className='text-[#003C84] text-sm font-medium'>
                                                {kolProfileItem?.bRDescription?.rendered}
                                            </div>
                                        </div>
                                    </div>
                                    {/* video */}
                                    {kolProfileItem?.bRVideoLink?.value && (
                                        <div
                                            className={cn(
                                                'relative w-[70px] h-10',
                                                // 'relative w-[70px] h-10 rounded border-[3px] border-[#E2EDF6]',
                                                !kolProfileItem?.bRVideoNative?.boolValue &&
                                                    'video-pop'
                                            )}
                                            data-poster={kolProfileItem?.bRVideoLink?.value}
                                            onClick={() => {
                                                kolProfileItem?.bRVideoNative?.boolValue
                                                    ? videoNative(
                                                          kolProfileItem?.bRVideoNative
                                                              ?.boolValue ?? false,
                                                          kolProfileItem?.bRVideoNativePip
                                                              ?.boolValue ?? false,
                                                          kolProfileItem?.bRVideoLink?.value ?? '',
                                                          '',
                                                          'Masterpiece'
                                                      )
                                                    : videoNoNative('Masterpiece', '', '');
                                            }}
                                        >
                                            <img
                                                className={`w-[22px] h-[22px] absolute right-[-5px] bottom-[-4px] z-10 overflow-hidden`}
                                                src={withBasePath('/images/kol/i-PlayV.svg')}
                                                alt=''
                                                loading='lazy'
                                            />
                                            <img
                                                className={`w-full h-full object-cover rounded border-[3px] border-[#E2EDF6]`}
                                                src={withFullPath(
                                                    kolProfileItem?.bRVideoPoster?.src || ''
                                                )}
                                                alt=''
                                                loading='lazy'
                                            />
                                        </div>
                                    )}
                                </div>

                                <div
                                    className='text-sm text-black font-normal pt-2'
                                    dangerouslySetInnerHTML={{
                                        __html: kolProfileItem?.bRContent?.rendered || '',
                                    }}
                                ></div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default memo(LeaderBoardItem);
